/**
 * Glove pose + target offset per controller state (owner: movement team). The integrator springs the glove toward
 * the target (GLOVE.follow) and clamps it to GLOVE.maxReach. Offsets are in FACING space (+x = in front).
 *
 * Poses (design/exports/highlights/5-glove-pose-test.png, "B · READY"):
 *   READY  default, side-view glove at chin height pointing at the opponent
 *   GRIP   holding / winding up / throwing the ball
 *   FIST   stiff-arm, celebration fist pump, special pose without the ball
 *   REACH  dive / leap lunges (toward the ball on a catch lunge)
 *   OPEN   tracking a ball in flight this player may touch (the offense receiver catches with it; a grounded
 *          defender's passive OPEN glove only swats — the defense intercepts with a committed glove: REACH or
 *          airborne, see ball/reach.ts `mayCatchFlight`); flailing when stunned
 */
import { FRAMES, GLOVE, MOTION } from '@hf/shared';
import { GlovePose, PlayerState } from '../enums';
import { ballInPlayAir, gloveFree, looseInPlay, mayRecoverLoose, mayTouchFlight } from '../ball/reach';
import { PF } from './fsm';
import { holdsBall } from './ctx';
import type { PlayerSim, SimState } from '../state';

interface Offset {
  readonly ox: number;
  readonly oy: number;
}

/** Pose + target from a GLOVE offset (objects, not doubles, cross the call: nothing is boxed). */
const set = (p: PlayerSim, pose: GlovePose, o: Offset): void => {
  p.glovePose = pose;
  p.gloveTargetX = o.ox;
  p.gloveTargetY = o.oy;
};

/** Facing-space vector from the head to the ball, clamped to the glove's reach. Returns false if out of range. */
const aimAtBall = (s: SimState, p: PlayerSim, pose: GlovePose): boolean => {
  const b = s.ball;
  const dx = (b.x - p.x) * p.facing;
  const dy = b.y - p.y;
  const d2 = dx * dx + dy * dy;
  if (d2 > MOTION.gloveTrackRange * MOTION.gloveTrackRange) return false;
  const d = Math.sqrt(d2);
  const k = d > GLOVE.maxReach ? GLOVE.maxReach / d : 1;
  p.glovePose = pose;
  p.gloveTargetX = dx * k;
  p.gloveTargetY = dy * k;
  return true;
};

/** A live flight this player may touch, or a loose kick he may pick up (the glove scoops toward it). */
const tracking = (s: SimState, p: PlayerSim): boolean =>
  gloveFree(p) && ((ballInPlayAir(s) && mayTouchFlight(s, p)) || (looseInPlay(s) && mayRecoverLoose(s, p)));

export const updateGloveTarget = (s: SimState, p: PlayerSim): void => {
  const holding = holdsBall(s, p);
  const f = p.stateFrame;
  switch (p.state) {
    case PlayerState.CHARGING_THROW:
      set(p, GlovePose.GRIP, GLOVE.windup);
      return;
    case PlayerState.THROW_RELEASE:
      if ((p.flags & PF.THROWN) === 0) set(p, GlovePose.GRIP, GLOVE.release);
      else if (f <= FRAMES.throwRelease.startup + FRAMES.throwRelease.active + 4)
        set(p, GlovePose.OPEN, GLOVE.release);
      else set(p, GlovePose.READY, GLOVE.idle);
      return;
    case PlayerState.PUMP_FAKE:
      if (f * 2 <= FRAMES.pumpFake.recovery) set(p, GlovePose.GRIP, GLOVE.release);
      else set(p, GlovePose.GRIP, GLOVE.holdBall);
      return;
    case PlayerState.STIFF_ARM:
      if (f <= FRAMES.stiffArm.startup + FRAMES.stiffArm.active) set(p, GlovePose.FIST, GLOVE.stiffArm);
      else set(p, holding ? GlovePose.GRIP : GlovePose.READY, GLOVE.holdBall);
      return;
    case PlayerState.TRUCK_CHARGE:
    case PlayerState.TRUCK_BURST:
      set(p, holding ? GlovePose.GRIP : GlovePose.FIST, GLOVE.truck);
      return;
    case PlayerState.DIVE:
      if (f <= FRAMES.dive.startup) set(p, GlovePose.READY, GLOVE.idle);
      else if ((p.flags & PF.VS_CARRIER) !== 0 || !tracking(s, p) || !aimAtBall(s, p, GlovePose.REACH))
        set(p, GlovePose.REACH, GLOVE.dive);
      return;
    case PlayerState.LEAP_GRAB:
      if ((p.flags & PF.VS_CARRIER) !== 0 || !tracking(s, p) || !aimAtBall(s, p, GlovePose.REACH))
        set(p, GlovePose.REACH, GLOVE.leap);
      return;
    case PlayerState.FAKE_DIVE:
      set(p, GlovePose.READY, GLOVE.idle);
      return;
    case PlayerState.FAKE_FREEZE:
    case PlayerState.FAKE_SLAM:
      // Design 02: "fist + head slam" — the ball is tucked while the glove clenches (render shows the fist).
      set(p, holding ? GlovePose.GRIP : GlovePose.FIST, GLOVE.reachUp);
      return;
    case PlayerState.CELEBRATE:
    case PlayerState.SPECIAL_POSE:
      set(p, holding ? GlovePose.GRIP : GlovePose.FIST, GLOVE.reachUp);
      return;
    case PlayerState.HARD_COUNT:
      if (f * 2 <= FRAMES.hardCount.recovery) set(p, GlovePose.GRIP, GLOVE.release);
      else set(p, GlovePose.GRIP, GLOVE.holdBall);
      return;
    case PlayerState.STUNNED:
      set(p, holding ? GlovePose.GRIP : GlovePose.OPEN, GLOVE.reachUp);
      return;
    case PlayerState.TACKLED:
      set(p, holding ? GlovePose.GRIP : GlovePose.OPEN, GLOVE.holdBall);
      return;
    default:
  }
  if (holding) {
    set(p, GlovePose.GRIP, GLOVE.holdBall);
    return;
  }
  if (tracking(s, p) && aimAtBall(s, p, GlovePose.OPEN)) return;
  const moving = p.state === PlayerState.RUN || p.state === PlayerState.DASH || !p.grounded;
  if (p.state === PlayerState.AIRBORNE && p.airFrame <= FRAMES.fakeCancel.windowEnd && p.vy > 0)
    set(p, GlovePose.READY, GLOVE.reachUp); // the glove flings up on take-off
  else if (moving) set(p, GlovePose.READY, GLOVE.run);
  else set(p, GlovePose.READY, GLOVE.idle);
};
